import { useEffect, useState } from "react";
import {
  Box, Paper, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CodeNameMasterApi, CodeNameMasterDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

interface Props {
  /** Plural page title, e.g. "Admission Types". */
  title: string;
  /** Singular noun for buttons and dialogs, e.g. "Admission type". */
  noun: string;
  subtitle?: string;
  api: CodeNameMasterApi;
  /** react-query cache key, e.g. "admission-types". */
  queryKey: string;
  /** Permission code prefix — the master uses a view/manage pair, e.g. "catalog.admissiontype". */
  permPrefix: string;
}

/**
 * Shared CRUD screen for the code/name rate-dimension masters (admission type, person type).
 * They are the same shape down to the display-order field, so they share one page rather than
 * two near-identical copies — the same reason ReferenceNameMasterPage exists for the item masters.
 */
export function CodeNameMasterPage({ title, noun, subtitle, api, queryKey, permPrefix }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CodeNameMasterDto | null>(null);

  const canManage = useAuth(s => s.hasPermission(`${permPrefix}.manage`));

  const query = useQuery({
    queryKey: [queryKey, search, pagination.page, pagination.pageSize],
    queryFn: () => api.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: `${noun} deleted` },
    mutationFn: (id: string) => api.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: CodeNameMasterDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: CodeNameMasterDto) => {
    if (confirm(`Delete ${noun.toLowerCase()} "${row.code} — ${row.name}"? Blocked if any charge row uses it.`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const columns: GridColDef<CodeNameMasterDto>[] = [
    { field: "code", headerName: "Code", width: 130 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "description", headerName: "Description", flex: 1.5, minWidth: 220,
      valueGetter: (_v, row) => row.description || "—" },
    { field: "displayOrder", headerName: "Order", width: 90, align: "right", headerAlign: "right",
      valueGetter: (_v, row) => row.displayOrder ?? "—" },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => <StatusPill label={p.row.isActive ? "Active" : "Inactive"} />,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title={title}
        subtitle={subtitle}
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New {noun.toLowerCase()}
          </Button>
        ) : undefined}
      />
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <CodeNameDialog
        open={formOpen} onClose={() => setFormOpen(false)}
        row={editing} noun={noun} api={api} queryKey={queryKey}
      />
    </Box>
  );
}

function CodeNameDialog({
  open, onClose, row, noun, api, queryKey,
}: {
  open: boolean; onClose: () => void; row: CodeNameMasterDto | null;
  noun: string; api: CodeNameMasterApi; queryKey: string;
}) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setCode(row?.code ?? "");
    setName(row?.name ?? "");
    setDescription(row?.description ?? "");
    setDisplayOrder(row?.displayOrder != null ? String(row.displayOrder) : "");
    setIsActive(row?.isActive ?? true);
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? `${noun} updated` : `${noun} created` },
    mutationFn: async () => {
      const payload = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        displayOrder: displayOrder === "" ? null : Number(displayOrder),
        isActive,
      };
      if (isEdit && row) { await api.update(row.id, payload); return row.id; }
      const r = await api.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      onClose();
    },
  });

  const err = !code.trim() ? "Code required" : !name.trim() ? "Name required" : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? `Edit ${noun.toLowerCase()}` : `New ${noun.toLowerCase()}`}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value)}
                inputProps={{ maxLength: 50 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 100 }} />
            </Grid>
            <Grid size={12}>
              <TextField label="Description" size="small" fullWidth multiline minRows={2}
                value={description} onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 500 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Display order" size="small" fullWidth type="number"
                value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)}
                helperText="Sorts the dropdown" />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormControlLabel label="Active"
                control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />} />
            </Grid>
          </Grid>
          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
